import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateTransformDuringAnimation, cancelTransformAnimation } from 'redux/mode/actions';
import { TransformAnimation } from 'redux/mode/reducers';

// This hook is like a tick-tock-clock that moves a transformation animation forward
// (if transformAnimation is provided)
//
// Usage: attach to one always-mounted component and it will
// push mode's animation forward as needed.
export const useTransformAnimation = (
	transformAnimation: TransformAnimation | null,
	activeSceneId: string | null
) => {
	const dispatch = useDispatch();

	useEffect(() => {
		if (!transformAnimation || !activeSceneId) return;

		let rafId: number;
		const animate = () => {
			const now = performance.now();
			const elapsed = now - transformAnimation.startTime;
			const progress = Math.min(elapsed / transformAnimation.duration, 1);

			if (progress < 1) {
				const t = transformAnimation.easing(progress);
				const currentTransform = {
					scale:
						transformAnimation.startTransform.scale +
						(transformAnimation.endTransform.scale - transformAnimation.startTransform.scale) * t,
					offset: {
						x:
							transformAnimation.startTransform.offset.x +
							(transformAnimation.endTransform.offset.x -
								transformAnimation.startTransform.offset.x) *
								t,
						y:
							transformAnimation.startTransform.offset.y +
							(transformAnimation.endTransform.offset.y -
								transformAnimation.startTransform.offset.y) *
								t,
					},
				};

				dispatch(
					updateTransformDuringAnimation({
						sceneId: activeSceneId,
						transform: currentTransform,
					})
				);

				rafId = requestAnimationFrame(animate);
			} else {
				// Animation complete
				dispatch(cancelTransformAnimation());
			}
		};

		rafId = requestAnimationFrame(animate);

		return () => {
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
		};
	}, [transformAnimation, activeSceneId, dispatch]);
};
